<template>
    <div
        class="editor-wrapper active-edit:right-96 fixed top-12 right-0 left-0 z-1000 overflow-visible border border-transparent border-b-gray-200 bg-white pr-4 text-center shadow-sm lg:top-0 lg:left-64"
    >
        <div class="absolute top-2 right-1 z-[1001] bg-white">
            <button
                class="flex-none cursor-pointer rounded-sm p-1 text-sm text-gray-500 hover:text-gray-700"
                @click="resetActiveBlockSelection(activeComponents, templateModel)"
            >
                <MdiClose class="h-6 w-6" />
            </button>
        </div>

        <div
            class="editor-panel md:overflow-x-none mx-auto flex min-h-14 w-full max-w-full flex-nowrap justify-start overflow-x-auto py-0.5 pr-6 pl-2 md:flex-wrap md:justify-center lg:max-w-5xl"
        >
            <!-- UNDO/REDO -->
            <div :class="panelStyle.section">
                <UndoButton :editor="editor" />
                <RedoButton :editor="editor" />
            </div>
            <!-- BASIC INLINE FORMATTING -->
            <div :class="panelStyle.section">
                <BoldTextButton
                    :editor="editor"
                    :editor-selection="editorSelection"
                />
                <ItalicTextButton
                    :editor="editor"
                    :editor-selection="editorSelection"
                />
                <StrikeTextButton
                    :editor="editor"
                    :editor-selection="editorSelection"
                />
                <UnderlineTextButton
                    :editor="editor"
                    :editor-selection="editorSelection"
                />
                <SuperscriptTextButton
                    :editor="editor"
                    :editor-selection="editorSelection"
                />
                <SubscriptTextButton
                    :editor="editor"
                    :editor-selection="editorSelection"
                />
            </div>
            <div :class="panelStyle.section">
                <FormatParagraphButton :editor="editor" />
                <FormatHeader1Button :editor="editor" />
                <FormatHeader2Button :editor="editor" />
                <FormatHeader3Button :editor="editor" />
                <FormatHeader4Button :editor="editor" />
                <FormatHeader5Button :editor="editor" />
                <FormatHeader6Button :editor="editor" />
            </div>
            <div
                v-if="templateModel.type === 'PDF'"
                :class="panelStyle.section"
            >
                <FontFamilySelect
                    :fonts="config.fonts"
                    :editor="editor"
                    :editor-selection="editorSelection"
                />
            </div>
            <div :class="panelStyle.section">
                <TextColorPicker
                    :editor="editor"
                    :editor-selection="editorSelection"
                />
                <BackgroundColorPicker
                    :editor="editor"
                    :editor-selection="editorSelection"
                />
                <ClearFormattingButton :editor="editor" />
            </div>
            <div :class="panelStyle.section">
                <LinkInsertButton
                    :editor="editor"
                    :editor-selection="editorSelection"
                    :link-insert-modal="linkInsertModal"
                />
                <LinkRemoveButton :editor="editor" />
            </div>
            <div :class="panelStyle.section">
                <UnorderedListButton :editor="editor" />
                <OrderedListButton :editor="editor" />
                <IndentDecreaseButton :editor="editor" />
                <IndentIncreaseButton :editor="editor" />
            </div>

            <div :class="panelStyle.section">
                <TextAlignLeftButton :editor="editor" />
                <TextAlignCenterButton :editor="editor" />
                <TextAlignRightButton :editor="editor" />
                <TextAlignJustifyButton :editor="editor" />
            </div>
            <div :class="panelStyle.section">
                <FormatBlockquoteButton :editor="editor" />
                <HorizontalRuleButton :editor="editor" />
                <ImageButton
                    v-if="['PDF', 'EMAIL'].includes(templateModel.type)"
                    :image-insert-modal="imageInsertModal"
                />
                <FormatPageBreakButton
                    v-if="['PDF'].includes(templateModel.type)"
                    :editor="editor"
                />
            </div>
            <div :class="panelStyle.section">
                <AddTableButton :editor="editor" />
                <DeleteTableButton :editor="editor" />
            </div>
            <div :class="panelStyle.section">
                <AddTableColumnBeforeButton :editor="editor" />
                <AddTableColumnAfterButton :editor="editor" />
                <DeleteTableColumnButton :editor="editor" />
            </div>
            <div :class="panelStyle.section">
                <AddTableRowBeforeButton :editor="editor" />
                <AddTableRowAfterButton :editor="editor" />
                <DeleteTableRowButton :editor="editor" />
            </div>
            <div :class="panelStyle.section">
                <TableMergeCellsButton :editor="editor" />
                <TableSplitCellsButton :editor="editor" />
            </div>
            <div :class="panelStyle.section">
                <TableToggleHeaderColumnButton :editor="editor" />
                <TableToggleHeaderRowButton :editor="editor" />
                <TableToggleHeaderCellButton :editor="editor" />
            </div>
        </div>
    </div>
    <ImageInsert :state="imageInsertModal.state" />
    <LinkInsert :state="linkInsertModal.state" />
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, watch, inject } from 'vue';
import ImageInsert from './ImageInsertSrc.vue';
import LinkInsert from './LinkInsert.vue';
import { t } from '../composables/i18n';
import { resetActiveBlockSelection } from '../composables/useWYSIWYGEditorUtils';
import MdiClose from 'materialdesignicons-vue3/icons/MdiClose.js';
import UndoButton from '../editor-widgets/toolbar-widgets/UndoButton.vue';
import RedoButton from '../editor-widgets/toolbar-widgets/RedoButton.vue';
import BoldTextButton from '../editor-widgets/toolbar-widgets/BoldTextButton.vue';
import ItalicTextButton from '../editor-widgets/toolbar-widgets/ItalicTextButton.vue';
import StrikeTextButton from '../editor-widgets/toolbar-widgets/StrikeTextButton.vue';
import UnderlineTextButton from '../editor-widgets/toolbar-widgets/UnderlineTextButton.vue';
import SuperscriptTextButton from '../editor-widgets/toolbar-widgets/SuperscriptTextButton.vue';
import SubscriptTextButton from '../editor-widgets/toolbar-widgets/SubscriptTextButton.vue';
import FontFamilySelect from '../editor-widgets/toolbar-widgets/FontFamilySelect.vue';
import LinkInsertButton from '../editor-widgets/toolbar-widgets/LinkInsertButton.vue';
import LinkRemoveButton from '../editor-widgets/toolbar-widgets/LinkRemoveButton.vue';
import TextColorPicker from '../editor-widgets/toolbar-widgets/TextColorPicker.vue';
import BackgroundColorPicker from '../editor-widgets/toolbar-widgets/BackgroundColorPicker.vue';
import ClearFormattingButton from '../editor-widgets/toolbar-widgets/ClearFormattingButton.vue';
import UnorderedListButton from '../editor-widgets/toolbar-widgets/UnorderedListButton.vue';
import OrderedListButton from '../editor-widgets/toolbar-widgets/OrderedListButton.vue';
import IndentDecreaseButton from '../editor-widgets/toolbar-widgets/IndentDecreaseButton.vue';
import IndentIncreaseButton from '../editor-widgets/toolbar-widgets/IndentIncreaseButton.vue';
import TextAlignLeftButton from '../editor-widgets/toolbar-widgets/TextAlignLeftButton.vue';
import TextAlignCenterButton from '../editor-widgets/toolbar-widgets/TextAlignCenterButton.vue';
import TextAlignRightButton from '../editor-widgets/toolbar-widgets/TextAlignRightButton.vue';
import TextAlignJustifyButton from '../editor-widgets/toolbar-widgets/TextAlignJustifyButton.vue';
import FormatParagraphButton from '../editor-widgets/toolbar-widgets/FormatParagraphButton.vue';
import FormatHeader1Button from '../editor-widgets/toolbar-widgets/FormatHeader1Button.vue';
import FormatHeader2Button from '../editor-widgets/toolbar-widgets/FormatHeader2Button.vue';
import FormatHeader3Button from '../editor-widgets/toolbar-widgets/FormatHeader3Button.vue';
import FormatHeader4Button from '../editor-widgets/toolbar-widgets/FormatHeader4Button.vue';
import FormatHeader5Button from '../editor-widgets/toolbar-widgets/FormatHeader5Button.vue';
import FormatHeader6Button from '../editor-widgets/toolbar-widgets/FormatHeader6Button.vue';
import FormatBlockquoteButton from '../editor-widgets/toolbar-widgets/FormatBlockquoteButton.vue';
import HorizontalRuleButton from '../editor-widgets/toolbar-widgets/HorizontalRuleButton.vue';
import ImageButton from '../editor-widgets/toolbar-widgets/ImageButton.vue';
import FormatPageBreakButton from '../editor-widgets/toolbar-widgets/FormatPageBreakButton.vue';
import AddTableButton from '../editor-widgets/toolbar-widgets/AddTableButton.vue';
import AddTableColumnBeforeButton from '../editor-widgets/toolbar-widgets/AddTableColumnBeforeButton.vue';
import AddTableColumnAfterButton from '../editor-widgets/toolbar-widgets/AddTableColumnAfterButton.vue';
import DeleteTableColumnButton from '../editor-widgets/toolbar-widgets/DeleteTableColumnButton.vue';
import AddTableRowBeforeButton from '../editor-widgets/toolbar-widgets/AddTableRowBeforeButton.vue';
import AddTableRowAfterButton from '../editor-widgets/toolbar-widgets/AddTableRowAfterButton.vue';
import DeleteTableRowButton from '../editor-widgets/toolbar-widgets/DeleteTableRowButton.vue';
import DeleteTableButton from '../editor-widgets/toolbar-widgets/DeleteTableButton.vue';
import TableMergeCellsButton from '../editor-widgets/toolbar-widgets/TableMergeCellsButton.vue';
import TableSplitCellsButton from '../editor-widgets/toolbar-widgets/TableSplitCellsButton.vue';
import TableToggleHeaderColumnButton from '../editor-widgets/toolbar-widgets/TableToggleHeaderColumnButton.vue';
import TableToggleHeaderRowButton from '../editor-widgets/toolbar-widgets/TableToggleHeaderRowButton.vue';
import TableToggleHeaderCellButton from '../editor-widgets/toolbar-widgets/TableToggleHeaderCellButton.vue';

defineExpose({
    name: 'WYSIWYGEditorToolbar',
});

const props = defineProps({
    editor: {
        type: Object,
        required: true,
    },
    active: {
        type: Boolean,
        default: true,
    },
});

const editor = props.editor;
const activeComponents = inject('activeComponents');
const templateVersionModel = inject('templateVersionModel');
const templateModel = inject('templateModel');

const panelStyle = reactive({
    section:
        'icon-stack flex pt-2 px-1 relative before:content-[""] before:block before:absolute before:top-3 before:left-0 before:w-0 before:h-6 before:border-dotted before:border-l before:border-l-zinc-300 first:before:border-l-0 first:before:border-none',
});

const editorSelection = reactive({
    text: '',
    from: 0,
    to: 0,
    fontFamily: '',
});

const imageInsertModal = reactive({
    state: {
        open: false,
        title: t('editorInsertImage'),
        text: t('editorInsertImage'),
        data: {
            templateId: templateVersionModel.templateId,
            name: templateModel.name,
            type: templateModel.type,
            editor: editor,
        },
        overlayPreventClose: true,
    },
});

const linkInsertModal = reactive({
    state: {
        open: false,
        title: t('editorInsertLink'),
        text: t('editorInsertLink'),
        data: {
            editor: editor,
        },
        overlayPreventClose: true,
    },
});

const editorPanelFixed = ref(true);
const handleScroll = () => {
    editorPanelFixed.value =
        window.top.scrollY > document.querySelector('.editor-panel').clientHeight;
};

watch(imageInsertModal.state, async () => {
    if (imageInsertModal.state.open === false) {
        delete imageInsertModal.state.data.image;
    }
});

editor.on('selectionUpdate', ({ editor }) => {
    const { view, state } = editor;
    const { from, to } = view.state.selection;
    editorSelection.from = from;
    editorSelection.to = to;
    editorSelection.text = state.doc.textBetween(from, to, '');
    state.doc.nodesBetween(from, to, (node) => {
        if (node.marks) {
            node.marks.forEach((mark) => {
                if (mark.attrs && Object.keys(mark.attrs).includes('fontFamily')) {
                    editorSelection.fontFamily = mark.attrs['fontFamily'];
                }
            });
        }
    });
});

onMounted(() => {
    window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});
</script>
